import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { UseFileHierarchyResult } from "../types";
import { getChildEntities, buildExpandedFoldersSet } from "../utils";

import { filterSupportedEntities } from "@/components/UnifiedRegistry/entity-labels";
import { useListFolderEntities } from "@/queries/folder-entities";
import { EntityType } from "@/queries/folder-entities/types";

export const useFileHierarchy = (): UseFileHierarchyResult => {
  const params = useParams();
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());

  // Consolidate entity ID extraction
  const currentEntityInfo = useMemo(() => {
    if (params.folderId) {
      return { type: EntityType.FOLDER, id: Number(params.folderId) };
    }
    if (params.promptId) {
      return { type: EntityType.PROMPT, id: Number(params.promptId) };
    }
    if (params.workflowId) {
      return { type: EntityType.WORKFLOW, id: Number(params.workflowId) };
    }
    if (params.datasetGroupId) {
      return { type: EntityType.DATASET, id: Number(params.datasetGroupId) };
    }
    if (params.blueprintId) {
      return { type: EntityType.REPORT, id: Number(params.blueprintId) };
    }
    if (params.releaseLabelGroupId) {
      return {
        type: EntityType.AB_TEST,
        id: Number(params.releaseLabelGroupId),
      };
    }
    if (params.setId) {
      return {
        type: EntityType.INPUT_VARIABLE_SET,
        id: Number(params.setId),
      };
    }
    if (params.smartTableId) {
      return {
        type: EntityType.SMART_TABLE,
        id: params.smartTableId,
      };
    }
    if (params.snippetId) {
      return {
        type: EntityType.SNIPPET,
        id: Number(params.snippetId),
      };
    }
    if (params.collectionId) {
      return {
        type: EntityType.SKILL_COLLECTION,
        id: Number(params.collectionId),
      };
    }
    return null;
  }, [params]);

  // Fetch all folders and files in a flattened structure for building tree
  const { data: allData, isLoading } = useListFolderEntities({
    folder_id: null,
    enabled: true,
    flatten: true,
    keepPreviousData: true,
  });

  const supportedEntities = useMemo(
    () => filterSupportedEntities(allData?.entities ?? []),
    [allData?.entities],
  );

  // Auto-expand folders in the path to current entity
  useEffect(() => {
    if (!supportedEntities.length || !currentEntityInfo) {
      return;
    }

    const newExpandedFolders = buildExpandedFoldersSet(supportedEntities, currentEntityInfo);

    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      newExpandedFolders.forEach(id => newSet.add(id));
      return newSet;
    });
  }, [currentEntityInfo, supportedEntities]);

  const toggleFolder = useCallback((folderId: number) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  const collapseAllFolders = useCallback(() => {
    setExpandedFolders(new Set());
  }, []);

  // Get children of a folder
  const getChildren = useCallback(
    (parentId: number | null) => getChildEntities(supportedEntities, parentId),
    [supportedEntities],
  );

  const rootItems = getChildren(null);

  return {
    expandedFolders,
    toggleFolder,
    collapseAllFolders,
    rootItems,
    isLoading,
    getChildren,
    allEntities: supportedEntities,
  };
};
